import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const GoalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [graphPoints, setGraphPoints] = useState([]);
  const [previousYearPoints, setPreviousYearPoints] = useState([]);
  const [stats, setStats] = useState(null);
  const [nextGoalTarget, setNextGoalTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentValue, setCurrentValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);
  const chartInstanceRef = useRef(null);
  const [showPreviousYear, setShowPreviousYear] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all'); // Mostrar todos por padrão
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [editAchievementValue, setEditAchievementValue] = useState('');
  const [editTargetValue, setEditTargetValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editLogs, setEditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Conclude modal state (para metas manuais)
  const [concludeModalOpen, setConcludeModalOpen] = useState(false);
  const [concludeCreateNext, setConcludeCreateNext] = useState(false);
  const [concludeTargetValue, setConcludeTargetValue] = useState('');
  const [isConcluding, setIsConcluding] = useState(false);

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0,00';
    return parseFloat(num).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const parseBrazilianNumber = (str) => {
    if (!str) return 0;
    const cleaned = String(str).replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Parsear como string para evitar problemas de timezone
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const getPeriodLabel = (period) => {
    const labels = {
      daily: 'Diária',
      weekly: 'Semanal',
      monthly: 'Mensal',
      quarterly: 'Trimestral',
      annual: 'Anual'
    };
    return labels[period] || period;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Ativa',
      concluded: 'Concluída',
      inactive: 'Inativa',
      excluded: 'Excluída',
      skipped: 'Pulada'
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'bg-success',
      concluded: 'bg-info',
      inactive: 'bg-secondary',
      excluded: 'bg-danger',
      skipped: 'bg-warning'
    };
    return classes[status] || 'bg-secondary';
  };

  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    // Parsear data sem timezone para evitar problemas
    const dateStr = typeof endDate === 'string' ? endDate : endDate.toISOString();
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const end = new Date(year, month - 1, day, 23, 59, 59);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = end - today;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const loadGoalDetail = useCallback(async (includeAll = false, isInitial = false) => {
    try {
      // Só mostrar loading na carga inicial, não durante atualizações de filtro
      if (isInitial) {
        setLoading(true);
      }
      
      const params = new URLSearchParams({ detail: 'true' });
      if (includeAll) {
        params.append('include_all', 'true');
      }
      
      console.log(`🔄 Carregando dados: include_all=${includeAll}`);
      const response = await axios.get(`/goals/${id}?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data;
        console.log(`✅ Dados recebidos: ${data.graph_points?.length || 0} pontos`);
        
        setGoal(data.goal);
        setGraphPoints(data.graph_points || []);
        setPreviousYearPoints(data.previous_year_points || []);
        setStats(data.stats || null);
        setNextGoalTarget(data.next_goal_target || null);
        
        // Inicializar valor atual formatado
        if (data.goal.current_value) {
          setCurrentValue(formatNumber(data.goal.current_value));
        }
      }
    } catch (error) {
      console.error('Error loading goal detail:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Carregar dados inicialmente ou quando filtro muda
  const isFirstLoad = useRef(true);
  
  useEffect(() => {
    loadGoalDetail(showExcluded, isFirstLoad.current);
    isFirstLoad.current = false;
  }, [loadGoalDetail, showExcluded]);

  // Renderizar gráfico
  useEffect(() => {
    if (!goal || !graphPoints || graphPoints.length === 0) {
      return;
    }

    // Aguardar Chart.js estar disponível
    if (typeof window === 'undefined' || !window.Chart) {
      // Tentar carregar Chart.js dinamicamente
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
      script.onload = () => {
        renderChart();
      };
      document.head.appendChild(script);
      return;
    }

    renderChart();

    function renderChart() {
      const ctx = document.getElementById('relatedGoalsChart');
      if (!ctx) return;

      // Destruir gráfico anterior
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

    // Filtrar pontos baseado no filtro de período
    let filteredPoints = [...graphPoints];
    const now = new Date();
    
    switch (periodFilter) {
      case 'week':
        filteredPoints = filteredPoints.filter(p => {
          const pointDate = new Date(p.date);
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return pointDate >= weekAgo;
        });
        break;
      case 'month':
        filteredPoints = filteredPoints.filter(p => {
          const pointDate = new Date(p.date);
          const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          return pointDate >= monthAgo;
        });
        break;
      case 'quarter':
        filteredPoints = filteredPoints.filter(p => {
          const pointDate = new Date(p.date);
          const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          return pointDate >= quarterAgo;
        });
        break;
      case 'year':
        filteredPoints = filteredPoints.filter(p => {
          const pointDate = new Date(p.date);
          return pointDate.getFullYear() === now.getFullYear();
        });
        break;
      case 'last3':
        filteredPoints = filteredPoints.slice(-3);
        break;
      case 'last6':
        filteredPoints = filteredPoints.slice(-6);
        break;
      case 'last12':
        filteredPoints = filteredPoints.slice(-12);
        break;
      case 'last24':
        filteredPoints = filteredPoints.slice(-24);
        break;
      case 'last36':
        filteredPoints = filteredPoints.slice(-36);
        break;
      default:
        break;
    }

    const labels = filteredPoints.map(p => p.period);
    const values = filteredPoints.map(p => p.value);
    const targets = filteredPoints.map(p => p.target);

    // Criar dados do ano anterior para cada ponto filtrado
    const previousYearValues = filteredPoints.map(p => {
      const matchingPrev = previousYearPoints.find(prev => prev.currentPeriod === p.period);
      return matchingPrev ? matchingPrev.previousValue : null;
    });

      const isNegative = goal.is_positive_goal === false;

      // Construir datasets
      const datasets = [
        {
          label: 'Valor da Meta',
          data: targets,
          borderColor: 'rgb(197, 165, 114)',
          backgroundColor: 'rgba(197, 165, 114, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: 'rgb(197, 165, 114)'
        },
        {
          label: 'Valor Realizado',
          data: values,
          borderColor: isNegative ? 'rgb(220, 53, 69)' : 'rgb(40, 167, 69)',
          backgroundColor: isNegative ? 'rgba(220, 53, 69, 0.1)' : 'rgba(40, 167, 69, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: true,
          pointRadius: 5,
          pointBackgroundColor: isNegative ? 'rgb(220, 53, 69)' : 'rgb(40, 167, 69)'
        }
      ];

      // Adicionar linha do ano anterior se ativado
      if (showPreviousYear && previousYearValues.some(v => v !== null)) {
        datasets.push({
          label: 'Ano Anterior',
          data: previousYearValues,
          borderColor: 'rgb(108, 117, 125)',
          backgroundColor: 'rgba(108, 117, 125, 0.05)',
          borderWidth: 2,
          borderDash: [3, 3],
          tension: 0.4,
          fill: false,
          pointRadius: 3,
          pointBackgroundColor: 'rgb(108, 117, 125)',
          pointStyle: 'triangle'
        });
      }

      const newChart = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat('pt-BR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  }).format(context.parsed.y);
                  label += ` ${goal.unit || ''}`;
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: `Valor (${goal.unit || ''})`
            },
            ticks: {
              callback: function(value) {
                return new Intl.NumberFormat('pt-BR', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 0 
                }).format(value);
              }
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
      });

      chartInstanceRef.current = newChart;
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [goal, graphPoints, previousYearPoints, periodFilter, showPreviousYear]);

  const handleQuickAdd = (percentage) => {
    if (!goal) return;
    const target = parseFloat(goal.target_value || 0);
    const current = parseBrazilianNumber(currentValue);
    const addValue = (target * percentage) / 100;
    const newValue = current + addValue;
    setCurrentValue(formatNumber(newValue));
  };

  const handleUpdateProgress = async (e) => {
    e.preventDefault();
    if (!goal || goal.status === 'concluded') return;

    setIsUpdating(true);
    setUpdateMessage(null);

    try {
      const valueDecimal = parseBrazilianNumber(currentValue);
      
      const response = await axios.post(`/goals/${goal.id}/progress`, {
        current_value_decimal: valueDecimal
      });

      if (response.data.success) {
        // Atualizar o goal localmente sem recarregar a página
        setGoal(prev => ({
          ...prev,
          current_value: valueDecimal,
          progress_percentage: response.data.progress_percentage
        }));
        
        setUpdateMessage({ type: 'success', text: 'Salvo com sucesso!' });
        setTimeout(() => setUpdateMessage(null), 2000);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      setUpdateMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Erro ao salvar' 
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConclude = async () => {
    if (!goal || goal.status === 'concluded' || goal.status === 'inactive') return;

    // Meta automática: concluir direto e navegar para a nova meta
    if (goal.repeat_type === 'auto') {
      if (!window.confirm('Deseja concluir esta meta? Uma nova meta será criada automaticamente para o próximo período.')) return;
      
      setIsConcluding(true);
      try {
        const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
          action: 'conclude'
        });
        
        if (response.data.next_goal && response.data.next_goal.id) {
          navigate(`/goals/${response.data.next_goal.id}`);
        } else {
          await loadGoalDetail();
        }
      } catch (error) {
        console.error('Error concluding goal:', error);
        alert('Erro ao concluir meta');
      } finally {
        setIsConcluding(false);
      }
      return;
    }

    // Meta manual: abrir modal de conclusão
    setConcludeCreateNext(false);
    setConcludeTargetValue(formatNumber(goal.target_value));
    setConcludeModalOpen(true);
  };

  const handleConcludeConfirm = async () => {
    setIsConcluding(true);
    try {
      // Concluir a meta atual (pular auto-repeat se não quer criar próxima)
      const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
        action: 'conclude',
        skip_auto_repeat: !concludeCreateNext
      });

      // Se o usuário optou por criar próxima meta manual
      if (concludeCreateNext) {
        // Parsear o target value informado
        const newTarget = parseBrazilianNumber(concludeTargetValue);
        if (newTarget <= 0) {
          alert('Por favor, informe um valor alvo válido.');
          setIsConcluding(false);
          return;
        }

        // Criar nova meta manualmente via API
        try {
          const createResponse = await axios.post('/goals/create-next-period', {
            goal_id: goal.id,
            target_value: newTarget
          });

          if (createResponse.data.success && createResponse.data.next_goal) {
            setConcludeModalOpen(false);
            navigate(`/goals/${createResponse.data.next_goal.id}`);
            return;
          }
        } catch (createError) {
          console.error('Error creating next period goal:', createError);
          // Se falhar ao criar, pelo menos a conclusão já foi feita
        }
      }

      // Se auto-repeat do backend criou a meta, navegar para ela
      if (response.data.next_goal && response.data.next_goal.id) {
        setConcludeModalOpen(false);
        navigate(`/goals/${response.data.next_goal.id}`);
        return;
      }

      setConcludeModalOpen(false);
      await loadGoalDetail();
    } catch (error) {
      console.error('Error concluding goal:', error);
      alert('Erro ao concluir meta');
    } finally {
      setIsConcluding(false);
    }
  };

  const handleSkip = async () => {
    const periodLabel = getPeriodLabel(goal.period).toLowerCase();
    if (!window.confirm(`Tem certeza que deseja pular este período (${periodLabel})? A meta será marcada como pulada e uma nova será criada para o próximo período.`)) return;
    
    try {
      const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
        action: 'skip'
      });
      
      // Se criou uma nova meta para o próximo período, navegar para ela
      if (response.data.next_goal && response.data.next_goal.id) {
        navigate(`/goals/${response.data.next_goal.id}`);
      } else {
        await loadGoalDetail();
      }
    } catch (error) {
      console.error('Error skipping goal:', error);
      alert('Erro ao pular meta');
    }
  };

  // Criar nova meta para o próximo período (quando a meta atual já está concluída)
  const handleCreateNew = async () => {
    if (!window.confirm('Deseja criar uma nova meta para o próximo período?')) return;
    
    try {
      const createResponse = await axios.post('/goals/create-next-period', {
        goal_id: goal.id,
        target_value: goal.target_value
      });

      if (createResponse.data.success && createResponse.data.next_goal) {
        navigate(`/goals/${createResponse.data.next_goal.id}`);
      } else {
        alert('Não foi possível criar a nova meta. Pode ser que já exista uma meta ativa para o próximo período.');
        await loadGoalDetail();
      }
    } catch (error) {
      console.error('Error creating new goal:', error);
      alert('Erro ao criar nova meta');
    }
  };

  const handleGoBack = async () => {
    if (!window.confirm('Deseja voltar para o período anterior? Esta meta será inativada e a meta anterior será reativada.')) return;

    try {
      const parentGoalId = goal.parent_goal_id || goal.id;
      
      // Buscar a meta anterior (skipped, com start_date menor que a atual)
      const response = await axios.get(`/goals/previous-period`, {
        params: {
          parent_goal_id: parentGoalId,
          current_start_date: goal.start_date
        }
      });

      if (response.data.success && response.data.previous_goal) {
        // Inativar a meta atual
        await axios.post(`/goals/${goal.id}/toggle-status`, {
          action: 'deactivate'
        });

        // Reativar a meta anterior
        await axios.post(`/goals/${response.data.previous_goal.id}/toggle-status`, {
          action: 'reactivate_single'
        });

        // Navegar para a meta anterior
        navigate(`/goals/${response.data.previous_goal.id}`);
      } else {
        alert('Não há período anterior para voltar.');
      }
    } catch (error) {
      console.error('Error going back:', error);
      alert('Erro ao voltar para o período anterior');
    }
  };

  const handleToggleStatus = async () => {
    const action = goal.status === 'inactive' ? 'reactivate_all' : 'deactivate';
    const confirmMsg = goal.status === 'inactive'
      ? 'Tem certeza que deseja reativar esta meta e suas metas relacionadas?'
      : 'Tem certeza que deseja inativar esta meta e todas as metas relacionadas?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.post(`/goals/${goal.id}/toggle-status`, { action });
      await loadGoalDetail();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Erro ao alterar status da meta');
    }
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async (deleteRelated) => {
    setIsDeleting(true);
    try {
      const url = deleteRelated 
        ? `/goals/${goal.id}?delete_related=true` 
        : `/goals/${goal.id}`;
      await axios.delete(url);
      setDeleteModalOpen(false);
      navigate('/goals');
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Erro ao excluir meta');
      setIsDeleting(false);
    }
  };

  // Excluir uma meta específica (marcar como excluded)
  const handleExcludeGoal = async (goalId, e) => {
    e.stopPropagation(); // Evitar abrir o modal de edição
    if (!window.confirm('Deseja excluir esta meta do histórico?')) return;
    
    try {
      await axios.put(`/goals/${goalId}`, { status: 'excluded' });
      await loadGoalDetail(showExcluded);
    } catch (error) {
      console.error('Error excluding goal:', error);
      alert('Erro ao excluir meta');
    }
  };

  // Recuperar uma meta excluída ou pulada (marcar como concluded)
  const handleRecoverGoal = async (goalId, e) => {
    e.stopPropagation(); // Evitar abrir o modal de edição
    if (!window.confirm('Deseja recuperar esta meta?')) return;
    
    try {
      await axios.put(`/goals/${goalId}`, { status: 'concluded' });
      await loadGoalDetail(showExcluded);
    } catch (error) {
      console.error('Error recovering goal:', error);
      alert('Erro ao recuperar meta');
    }
  };

  const handleRowClick = async (point) => {
    setEditingPoint(point);
    setEditAchievementValue(formatNumber(point.value));
    setEditTargetValue(formatNumber(point.target));
    setEditNotes('');
    setEditLogs([]);
    setEditModalOpen(true);
    
    // Carregar histórico de logs
    setLoadingLogs(true);
    try {
      const response = await axios.get(`/goals/${point.goal_id}/logs`);
      if (response.data.success) {
        setEditLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingPoint(null);
    setEditAchievementValue('');
    setEditTargetValue('');
    setEditNotes('');
    setEditLogs([]);
  };

  const handleSaveEdit = async () => {
    if (!editingPoint) return;

    setIsSavingEdit(true);
    try {
      const achievementDecimal = parseBrazilianNumber(editAchievementValue);
      const targetDecimal = parseBrazilianNumber(editTargetValue);
      
      const achievementChanged = achievementDecimal !== editingPoint.value;
      const targetChanged = targetDecimal !== editingPoint.target;

      // Se o valor alvo mudou, atualizar via PUT
      if (targetChanged) {
        await axios.put(`/goals/${editingPoint.goal_id}`, {
          target_value: targetDecimal
        });
      }

      // Se o valor realizado mudou, atualizar via POST /progress
      if (achievementChanged) {
        let notes = editNotes;
        // Se o target também mudou, incluir no log
        if (targetChanged) {
          notes = `Meta ajustada: ${formatNumber(editingPoint.target)} → ${formatNumber(targetDecimal)}. ${notes || ''}`.trim();
        }
        
        await axios.post(`/goals/${editingPoint.goal_id}/progress`, {
          current_value_decimal: achievementDecimal,
          notes: notes
        });
      } else if (targetChanged && editNotes) {
        // Se só o target mudou e há notas, registrar log
        await axios.post(`/goals/${editingPoint.goal_id}/progress`, {
          current_value_decimal: achievementDecimal,
          notes: `Meta ajustada: ${formatNumber(editingPoint.target)} → ${formatNumber(targetDecimal)}. ${editNotes}`
        });
      }

      await loadGoalDetail();
      handleCloseEditModal();
    } catch (error) {
      console.error('Error saving edit:', error);
      alert(error.response?.data?.error || 'Erro ao salvar alterações');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Filtrar pontos para a tabela baseado em filtros de período e data
  // (precisa estar antes dos returns condicionais para seguir as regras dos Hooks)
  const filteredTablePoints = useMemo(() => {
    if (!graphPoints || graphPoints.length === 0) return [];
    
    let points = [...graphPoints];
    const now = new Date();

    // Aplicar filtro de período
    switch (periodFilter) {
      case 'week':
        points = points.filter(p => {
          const pointDate = new Date(p.date);
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return pointDate >= weekAgo;
        });
        break;
      case 'month':
        points = points.filter(p => {
          const pointDate = new Date(p.date);
          const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          return pointDate >= monthAgo;
        });
        break;
      case 'quarter':
        points = points.filter(p => {
          const pointDate = new Date(p.date);
          const quarterAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          return pointDate >= quarterAgo;
        });
        break;
      case 'year':
        points = points.filter(p => {
          const pointDate = new Date(p.date);
          return pointDate.getFullYear() === now.getFullYear();
        });
        break;
      case 'last3':
        points = points.slice(-3);
        break;
      case 'last6':
        points = points.slice(-6);
        break;
      case 'last12':
        points = points.slice(-12);
        break;
      case 'last24':
        points = points.slice(-24);
        break;
      case 'last36':
        points = points.slice(-36);
        break;
      default:
        // 'all' - não filtra
        break;
    }

    // Aplicar filtro de data de início
    if (dateStart) {
      const startDate = new Date(dateStart);
      points = points.filter(p => new Date(p.date) >= startDate);
    }

    // Aplicar filtro de data de fim
    if (dateEnd) {
      const endDate = new Date(dateEnd);
      endDate.setHours(23, 59, 59, 999); // Incluir o dia inteiro
      points = points.filter(p => new Date(p.date) <= endDate);
    }

    return points;
  }, [graphPoints, periodFilter, dateStart, dateEnd]);

  if (loading) {
    return (
      <div className="goals-list-page">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="goals-list-page container-fluid py-4">
        <div className="no-goals">
          <p>Meta não encontrada</p>
          <Link to="/goals" className="goals-btn goals-btn-primary mt-3">
            <i className="fas fa-arrow-left"></i> Voltar à Lista
          </Link>
        </div>
      </div>
    );
  }

  const progressPercentage = goal.target_value > 0 
    ? (parseFloat(goal.current_value || 0) / parseFloat(goal.target_value)) * 100 
    : 0;
  
  const daysRemaining = calculateDaysRemaining(goal.end_date);
  const isNegative = goal.is_positive_goal === false;

  return (
    <div className="goals-list-page container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h1 className="h2 mb-2" style={{ color: '#fff' }}>Detalhes da Meta</h1>
              <p className="mb-0" style={{ color: '#c5a572' }}>{goal.title}</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Link to="/goals" className="goals-btn-goals goals-btn-primary-goals btn-action-secondary">
                <div className="btn-content-secondary">
                  <i className="fas fa-arrow-left btn-icon-secondary"></i>
                  <span className="btn-label-secondary d-none d-sm-inline">Voltar à Lista</span>
                </div>
              </Link>
              <Link to="/dashboard" className="goals-btn-goals goals-btn-primary-goals btn-action-secondary">
                <div className="btn-content-secondary">
                  <i className="fas fa-chart-bar btn-icon-secondary"></i>
                  <span className="btn-label-secondary d-none d-sm-inline">Dashboard</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Left: Goal Card */}
        <div className="col-lg-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <span className="badge bg-primary me-2">{getPeriodLabel(goal.period)}</span>
                  
                  {goal.department_name && (
                    <span className="badge bg-info me-2">
                      <i className="fas fa-building me-1"></i>{goal.department_name}
                    </span>
                  )}

                  <span className={`badge ${getStatusBadgeClass(goal.status)}`}>
                    {getStatusLabel(goal.status)}
                  </span>

                  {goal.repeat_type === 'auto' && (
                    <span className="badge bg-warning me-2">
                      <i className="fas fa-sync-alt me-1"></i>Automática
                    </span>
                  )}

                  <h2 className="h4 mt-2 mb-1">{goal.title}</h2>

                  {goal.description && (
                    <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{goal.description}</p>
                  )}
                </div>
              </div>

              <div className="row g-3">
                {/* Info Card */}
                <div className="col-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Início</small>
                          <strong>{formatDate(goal.start_date)}</strong>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Término</small>
                          <strong>{formatDate(goal.end_date)}</strong>
                        </div>
                        <div className="col-12">
                          <small className="text-muted d-block">Dias Restantes</small>
                          <strong>{daysRemaining > 0 ? `${daysRemaining} dias` : 'Expirada'}</strong>
                        </div>
                        {goal.operation_name && (
                          <>
                            <div className="col-6">
                              <small className="text-muted d-block">Operação</small>
                              <strong>{goal.operation_name}</strong>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Unidade</small>
                              <strong>{goal.unit_name}</strong>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Card */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Progresso</span>
                        <span className={`h5 mb-0 ${isNegative ? 'text-danger' : 'text-primary'}`}>
                          {progressPercentage.toFixed(1)}%
                        </span>
                      </div>

                      <div className="goals-progress-bar mb-3">
                        <div 
                          className={`goals-progress-fill ${progressPercentage >= 100 ? 'exceeded' : ''} ${isNegative ? 'negative-goal' : ''}`}
                          role="progressbar"
                          style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                          aria-valuenow={progressPercentage}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted">
                          {formatNumber(goal.current_value)} / {formatNumber(goal.target_value)} {goal.unit || ''}
                        </span>
                        <span className="text-muted">
                          {isNegative 
                            ? (parseFloat(goal.current_value) <= parseFloat(goal.target_value) 
                                ? 'Cuidado para não passar do limite' 
                                : 'Limite ultrapassado')
                            : (progressPercentage >= 100 
                                ? 'Meta atingida!' 
                                : `${formatNumber(parseFloat(goal.target_value) - parseFloat(goal.current_value || 0))} restantes`)
                          }
                        </span>
                      </div>

                      {goal.status === 'active' && (
                        <div className="mt-3">
                          {updateMessage && (
                            <div className={`alert alert-${updateMessage.type === 'success' ? 'success' : 'danger'}`} style={{ 
                              padding: '0.5rem 0.75rem', 
                              marginBottom: '0.5rem', 
                              fontSize: '0.85rem'
                            }}>
                              {updateMessage.text}
                            </div>
                          )}
                          <form onSubmit={handleUpdateProgress} className="goals-progress-form">
                            <div className="d-flex flex-column gap-2">
                              <div className="d-flex gap-2">
                                <div className="flex-grow-1">
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    value={currentValue}
                                    onChange={(e) => setCurrentValue(e.target.value)}
                                    placeholder="123,45"
                                    pattern="[0-9.,]*"
                                    inputMode="decimal"
                                    required
                                    disabled={isUpdating}
                                  />
                                </div>
                                <button 
                                  type="submit" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-update-detail"
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-save"></i>
                                  <span>Salvar</span>
                                </button>
                              </div>
                              
                              <div className="d-flex flex-wrap gap-1 justify-content-center">
                                <button 
                                  type="button" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-quick-add-detail"
                                  onClick={() => handleQuickAdd(10)}
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-plus"></i>
                                  <span>10</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-quick-add-detail"
                                  onClick={() => handleQuickAdd(25)}
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-plus"></i>
                                  <span>25</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-quick-add-detail"
                                  onClick={() => handleQuickAdd(50)}
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-plus"></i>
                                  <span>50</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-quick-add-detail"
                                  onClick={() => handleQuickAdd(100)}
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-plus"></i>
                                  <span>100</span>
                                </button>
                                <button 
                                  type="button" 
                                  className="goals-btn-goals goals-btn-primary-goals goals-btn-quick-add-detail"
                                  onClick={() => setCurrentValue(formatNumber(goal.target_value))}
                                  disabled={isUpdating}
                                >
                                  <i className="fas fa-check"></i>
                                  <span>100%</span>
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Repeat Card */}
                {(goal.repeat_type === 'auto' || goal.repeat_type === 'manual') && (
                  <div className="col-12">
                    <div className="card bg-light">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-2">
                          <span className="me-2">🔄</span>
                          <span>
                            {goal.repeat_type === 'auto' 
                              ? 'Meta de repetição automática'
                              : 'Repetição manual'
                            }
                          </span>
                        </div>
                        {goal.repeat_type === 'auto' && goal.next_reset && (
                          <div className="small text-muted">
                            <span>Próxima redefinição: </span>
                            <span>{formatDate(goal.next_reset)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="col-12">
                  <div className="d-flex flex-column gap-3">
                    {/* Linha 1: Editar, Alvos */}
                    <div className="row g-2">
                      <div className="col-6">
                        <Link 
                          to={`/goals/${goal.id}/edit`} 
                          className="goals-btn-goals goals-btn-primary-goals btn-action-secondary w-100"
                        >
                          <div className="btn-content-secondary">
                            <i className="fas fa-pencil-alt btn-icon-secondary"></i>
                            <span className="btn-label-secondary">Editar</span>
                          </div>
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link 
                          to={`/goals/${goal.id}/targets`} 
                          className="goals-btn-goals goals-btn-primary-goals btn-action-secondary w-100"
                        >
                          <div className="btn-content-secondary">
                            <i className="fas fa-bullseye btn-icon-secondary"></i>
                            <span className="btn-label-secondary">Alvos</span>
                          </div>
                        </Link>
                      </div>
                    </div>
                    {/* Linha 2: Concluir, Voltar, Pular */}
                    <div className="row g-2">
                      <div className="col-4">
                        {goal.status === 'concluded' ? (
                          <button
                            type="button"
                            className="goals-btn-goals goals-btn-success-goals btn-action-secondary w-100"
                            onClick={handleCreateNew}
                          >
                            <div className="btn-content-secondary">
                              <i className="fas fa-plus-circle btn-icon-secondary"></i>
                              <span className="btn-label-secondary">Nova</span>
                            </div>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="goals-btn-goals goals-btn-primary-goals btn-action-secondary w-100"
                            onClick={handleConclude}
                            disabled={goal.status === 'inactive'}
                          >
                            <div className="btn-content-secondary">
                              <i className="fas fa-check-circle btn-icon-secondary"></i>
                              <span className="btn-label-secondary">Concluir</span>
                            </div>
                          </button>
                        )}
                      </div>
                      <div className="col-4">
                        <button
                          type="button"
                          className="goals-btn-goals goals-btn-secondary-goals btn-action-secondary w-100"
                          onClick={handleGoBack}
                          disabled={goal.status !== 'active'}
                          title="Voltar para o período anterior"
                        >
                          <div className="btn-content-secondary">
                            <i className="fas fa-backward btn-icon-secondary"></i>
                            <span className="btn-label-secondary">Voltar</span>
                          </div>
                        </button>
                      </div>
                      <div className="col-4">
                        <button
                          type="button"
                          className="goals-btn-goals goals-btn-warning-goals btn-action-secondary w-100"
                          onClick={handleSkip}
                          disabled={goal.status !== 'active'}
                        >
                          <div className="btn-content-secondary">
                            <i className="fas fa-forward btn-icon-secondary"></i>
                            <span className="btn-label-secondary">Pular</span>
                          </div>
                        </button>
                      </div>
                    </div>
                    {/* Linha 2: Pausar/Reativar, Excluir */}
                    <div className="row g-2">
                      <div className="col-6">
                        <button
                          type="button"
                          className={`goals-btn-goals ${goal.status === 'inactive' ? 'goals-btn-primary-goals' : 'goals-btn-warning-goals'} btn-action-secondary w-100`}
                          onClick={handleToggleStatus}
                        >
                          <div className="btn-content-secondary">
                            <i className={`fas ${goal.status === 'inactive' ? 'fa-play-circle' : 'fa-pause-circle'} btn-icon-secondary`}></i>
                            <span className="btn-label-secondary">
                              {goal.status === 'inactive' ? 'Reativar' : 'Inativar'}
                            </span>
                          </div>
                        </button>
                      </div>
                      <div className="col-6">
                        <button
                          type="button"
                          className="goals-btn-goals goals-btn-danger-goals btn-action-secondary w-100"
                          onClick={handleDelete}
                        >
                          <div className="btn-content-secondary">
                            <i className="fas fa-trash-alt btn-icon-secondary"></i>
                            <span className="btn-label-secondary">Excluir</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Chart */}
        <div className="col-lg-8">
          <div className="card shadow-sm h-100" style={{ minHeight: '600px' }}>
            <div className="card-body d-flex flex-column" style={{ height: '100%', overflow: 'hidden' }}>
              <div className="chart-header mb-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3">
                  <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                    <h3 className="h5 mb-0">Progresso das Metas Relacionadas</h3>
                    <span className="text-muted small">
                      {showExcluded ? '(Incluindo excluídas e puladas)' : '(Apenas metas concluídas)'}
                    </span>
                  </div>
                  
                  {/* Toggle para mostrar ano anterior */}
                  {graphPoints.length > 0 && (
                    <div className="filter-toggle">
                      <input 
                        type="checkbox" 
                        id="showPreviousYear" 
                        className="filter-checkbox"
                        checked={showPreviousYear}
                        onChange={(e) => setShowPreviousYear(e.target.checked)}
                      />
                      <label htmlFor="showPreviousYear" className="toggle-label">
                        <span className="toggle-text">Mostrar Ano Anterior</span>
                        <div className="toggle-switch">
                          <div className="toggle-slider"></div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
                
                {graphPoints.length > 0 ? (
                  <div className="filters-container">
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                      <div className="d-flex align-items-center gap-2 filter-group">
                        <label htmlFor="periodFilter" className="form-label mb-0 fw-medium">Filtrar por:</label>
                        <select 
                          id="periodFilter" 
                          className="form-select form-select-sm period-filter-select"
                          value={periodFilter}
                          onChange={(e) => setPeriodFilter(e.target.value)}
                        >
                          <option value="all">Todos os Períodos</option>
                          <option value="week">Esta Semana</option>
                          <option value="month">Este Mês</option>
                          <option value="quarter">Este Trimestre</option>
                          <option value="year">Este Ano</option>
                          <option value="last3">Últimos 3 Períodos</option>
                          <option value="last6">Últimos 6 Períodos</option>
                          <option value="last12">Últimos 12 Períodos</option>
                          <option value="last24">Últimos 24 Períodos</option>
                          <option value="last36">Últimos 36 Períodos</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Não há metas concluídas para exibir. Os filtros estarão disponíveis quando houver histórico de períodos concluídos.
                  </div>
                )}
              </div>

              {/* Summary Cards */}
              {stats && (
                <>
                  <div className="row g-2 mb-2">
                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-success h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-success mb-1">
                            <i className="fas fa-trophy" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Melhor Resultado</h6>
                          <div className="h5 mb-0 text-success">
                            {stats.best_result ? `${stats.best_result.completion.toFixed(1)}%` : '-'}
                          </div>
                          <div className="small text-muted">
                            {stats.best_result 
                              ? `${formatNumber(stats.best_result.value)} / ${formatNumber(stats.best_result.target)} ${goal.unit || ''}`
                              : '-'
                            }
                          </div>
                          <small className="text-muted">{stats.best_result?.period || '-'}</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-danger h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-danger mb-1">
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Pior Resultado</h6>
                          <div className="h5 mb-0 text-danger">
                            {stats.worst_result ? `${stats.worst_result.completion.toFixed(1)}%` : '-'}
                          </div>
                          <div className="small text-muted">
                            {stats.worst_result 
                              ? `${formatNumber(stats.worst_result.value)} / ${formatNumber(stats.worst_result.target)} ${goal.unit || ''}`
                              : '-'
                            }
                          </div>
                          <small className="text-muted">{stats.worst_result?.period || '-'}</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-success h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-success mb-1">
                            <i className="fas fa-chart-line" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Média Geral</h6>
                          <div className="h5 mb-0 text-success">
                            {stats.average_result ? `${stats.average_result.toFixed(1)}%` : '-'}
                          </div>
                          <div className="small text-muted">
                            {stats.periods_count > 0 ? `Baseado em ${stats.periods_count} períodos` : '-'}
                          </div>
                          <small className="text-muted">Performance média</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-info h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-info mb-1">
                            <i className="fas fa-calculator" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Média Absoluta</h6>
                          <div className="h5 mb-0 text-info">
                            {stats.average_absolute ? formatNumber(stats.average_absolute) : '-'}
                          </div>
                          <div className="small text-muted">
                            Baseado em <span>{stats.periods_count || 0}</span> períodos
                          </div>
                          <small className="text-muted">Valor médio realizado</small>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-warning h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-warning mb-1">
                            <i className="fas fa-chart-bar" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Taxa de Sucesso</h6>
                          <div className="h5 mb-0 text-warning">
                            {stats.success_rate ? `${stats.success_rate.toFixed(1)}%` : '-'}
                          </div>
                          <div className="small text-muted">
                            {stats.periods_count > 0 
                              ? `${Math.round((stats.success_rate / 100) * stats.periods_count)} de ${stats.periods_count} períodos`
                              : '-'
                            }
                          </div>
                          <small className="text-muted">Metas alcançadas</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-primary h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-primary mb-1">
                            <i className="fas fa-arrow-right" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Próxima Meta</h6>
                          <div className="h5 mb-0 text-primary">
                            {nextGoalTarget 
                              ? `${formatNumber(nextGoalTarget.target)} ${goal.unit || ''}`
                              : (goal.repeat_type === 'auto' || goal.repeat_type === 'manual')
                                ? `${formatNumber(goal.target_value)} ${goal.unit || ''}`
                                : '-'
                            }
                          </div>
                          <small className="text-muted">
                            {nextGoalTarget 
                              ? formatDate(nextGoalTarget.date)
                              : '-'
                            }
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-success h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-success mb-1">
                            <i className="fas fa-fire" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Sequência Atual</h6>
                          <div className="h5 mb-0 text-success">{stats.current_streak || 0}</div>
                          <div className="small text-muted">Períodos consecutivos</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-warning h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-warning mb-1">
                            <i className="fas fa-star" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Melhor Sequência</h6>
                          <div className="h5 mb-0 text-warning">{stats.best_streak || 0}</div>
                          <div className="small text-muted">Recorde pessoal</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg col-md-3 col-sm-6">
                      <div className="card border-secondary h-100 summary-card">
                        <div className="card-body text-center p-2">
                          <div className="text-secondary mb-1">
                            <i className="fas fa-calendar-alt" style={{ fontSize: '1.25rem' }}></i>
                          </div>
                          <h6 className="card-title mb-1">Total Períodos</h6>
                          <div className="h5 mb-0 text-secondary">{stats.periods_count || 0}</div>
                          <div className="small text-muted">Períodos concluídos</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Chart Container */}
              <div className="chart-container" style={{ position: 'relative', flex: '1 1 0', minHeight: '300px', width: '100%' }}>
                {graphPoints.length > 0 ? (
                  <canvas id="relatedGoalsChart"></canvas>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center text-muted">
                      <i className="fas fa-chart-line fa-3x mb-3"></i>
                      <p>Nenhum dado disponível para exibir o gráfico</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* History Table */}
      {graphPoints.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-column gap-3 mb-4">
                  {/* Título e contagem */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <h3 className="h5 mb-0">Histórico de Atualizações</h3>
                      <span className="badge bg-primary" title={`${graphPoints.length} total`}>
                        {filteredTablePoints.length} / {graphPoints.length}
                      </span>
                    </div>
                    
                    {/* Toggle para mostrar excluídas/puladas */}
                    <div className="filter-toggle">
                      <input 
                        type="checkbox" 
                        id="showExcluded" 
                        className="filter-checkbox"
                        checked={showExcluded}
                        onChange={(e) => setShowExcluded(e.target.checked)}
                      />
                      <label htmlFor="showExcluded" className="toggle-label">
                        <span className="toggle-text">Incluir Excluídas/Puladas</span>
                        <div className="toggle-switch">
                          <div className="toggle-slider"></div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Filtros de período */}
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <label htmlFor="tablePeriodFilter" className="form-label mb-0 small fw-medium">Período:</label>
                      <select 
                        id="tablePeriodFilter" 
                        className="form-select form-select-sm"
                        style={{ width: 'auto', minWidth: '150px' }}
                        value={periodFilter}
                        onChange={(e) => setPeriodFilter(e.target.value)}
                      >
                        <option value="all">Todos</option>
                        <option value="week">Esta Semana</option>
                        <option value="month">Este Mês</option>
                        <option value="quarter">Este Trimestre</option>
                        <option value="year">Este Ano</option>
                        <option value="last3">Últimos 3</option>
                        <option value="last6">Últimos 6</option>
                        <option value="last12">Últimos 12</option>
                        <option value="last24">Últimos 24</option>
                        <option value="last36">Últimos 36</option>
                      </select>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <label htmlFor="dateStart" className="form-label mb-0 small fw-medium">De:</label>
                      <input 
                        type="date" 
                        id="dateStart"
                        className="form-control form-control-sm"
                        style={{ width: 'auto' }}
                        value={dateStart}
                        onChange={(e) => setDateStart(e.target.value)}
                      />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <label htmlFor="dateEnd" className="form-label mb-0 small fw-medium">Até:</label>
                      <input 
                        type="date" 
                        id="dateEnd"
                        className="form-control form-control-sm"
                        style={{ width: 'auto' }}
                        value={dateEnd}
                        onChange={(e) => setDateEnd(e.target.value)}
                      />
                    </div>

                    {(dateStart || dateEnd || periodFilter !== 'all') && (
                      <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setDateStart('');
                          setDateEnd('');
                          setPeriodFilter('all');
                        }}
                        title="Limpar filtros"
                      >
                        <i className="fas fa-times me-1"></i>
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Período</th>
                        <th>Status</th>
                        <th>Meta</th>
                        <th>Realizado</th>
                        <th>Alvo</th>
                        <th>Percentual</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTablePoints.map((point, index) => (
                        <tr 
                          key={index}
                          onClick={() => handleRowClick(point)}
                          style={{ cursor: 'pointer' }}
                          className="goal-history-row"
                          title="Clique para editar valores"
                        >
                          <td>{point.period}</td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(point.status)}`}>
                              {getStatusLabel(point.status)}
                            </span>
                          </td>
                          <td>{point.name}</td>
                          <td>{formatNumber(point.value)}</td>
                          <td>{formatNumber(point.target)}</td>
                          <td>
                            <div className="progress" style={{ height: '20px' }}>
                              <div 
                                className={`progress-bar ${
                                  isNegative
                                    ? (point.completion <= 80 ? 'bg-primary' : point.completion <= 100 ? 'bg-success' : point.completion <= 130 ? 'bg-warning' : 'bg-danger')
                                    : (point.completion >= 120 ? 'bg-primary' : point.completion >= 100 ? 'bg-success' : point.completion >= 70 ? 'bg-warning' : 'bg-danger')
                                }`}
                                role="progressbar"
                                style={{ width: `${Math.min(point.completion, 100)}%` }}
                                aria-valuenow={point.completion}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {point.completion.toFixed(1)}%
                              </div>
                            </div>
                          </td>
                          <td>
                            {point.status === 'concluded' && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => handleExcludeGoal(point.goal_id, e)}
                                title="Excluir esta meta"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                            {(point.status === 'excluded' || point.status === 'skipped') && (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={(e) => handleRecoverGoal(point.goal_id, e)}
                                title="Recuperar esta meta"
                              >
                                <i className="fas fa-undo"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Unified for Achievement and Target */}
      {editModalOpen && editingPoint && (
        <>
          <div 
            className="modal-backdrop fade show" 
            onClick={handleCloseEditModal}
            style={{ zIndex: 1040 }}
          ></div>
          <div 
            className="modal fade show" 
            style={{ display: 'block', zIndex: 1050 }} 
            tabIndex="-1"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseEditModal();
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-edit text-primary me-2"></i>Editar Período
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {/* Period Info */}
                <div className="alert alert-light mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>Período:</strong> {editingPoint.period}</span>
                    <span className={`badge ${getStatusBadgeClass(editingPoint.status)}`}>
                      {getStatusLabel(editingPoint.status)}
                    </span>
                  </div>
                  <div className="small text-muted mt-1">{editingPoint.name}</div>
                </div>

                {/* Achievement Value */}
                <div className="mb-3">
                  <label htmlFor="achievementValue" className="form-label">
                    <i className="fas fa-chart-line text-success me-1"></i>
                    Valor Realizado
                  </label>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control" 
                      id="achievementValue"
                      value={editAchievementValue}
                      onChange={(e) => setEditAchievementValue(e.target.value)}
                      placeholder="123,45"
                      pattern="[0-9.,]*"
                      inputMode="decimal"
                      required
                      disabled={isSavingEdit}
                    />
                    <span className="input-group-text">{goal.unit || ''}</span>
                  </div>
                </div>

                {/* Target Value */}
                <div className="mb-3">
                  <label htmlFor="targetValue" className="form-label">
                    <i className="fas fa-bullseye text-warning me-1"></i>
                    Valor da Meta
                  </label>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control" 
                      id="targetValue"
                      value={editTargetValue}
                      onChange={(e) => setEditTargetValue(e.target.value)}
                      placeholder="123,45"
                      pattern="[0-9.,]*"
                      inputMode="decimal"
                      required
                      disabled={isSavingEdit}
                    />
                    <span className="input-group-text">{goal.unit || ''}</span>
                  </div>
                </div>

                {/* Preview Progress */}
                {editAchievementValue && editTargetValue && (
                  <div className="mb-3">
                    <label className="form-label text-muted small">Prévia do Progresso</label>
                    <div className="progress" style={{ height: '24px' }}>
                      {(() => {
                        const achievement = parseBrazilianNumber(editAchievementValue);
                        const target = parseBrazilianNumber(editTargetValue);
                        const pct = target > 0 ? (achievement / target) * 100 : 0;
                        const isNeg = goal.is_positive_goal === false;
                        const barClass = isNeg
                          ? (pct <= 80 ? 'bg-primary' : pct <= 100 ? 'bg-success' : pct <= 130 ? 'bg-warning' : 'bg-danger')
                          : (pct >= 120 ? 'bg-primary' : pct >= 100 ? 'bg-success' : pct >= 70 ? 'bg-warning' : 'bg-danger');
                        return (
                          <div 
                            className={`progress-bar ${barClass}`}
                            role="progressbar"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          >
                            {pct.toFixed(1)}%
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* History Logs */}
                {(loadingLogs || editLogs.length > 0) && (
                  <div className="mb-3">
                    <label className="form-label">
                      <i className="fas fa-history text-secondary me-1"></i>
                      Histórico de Alterações
                      {editLogs.length > 0 && (
                        <span className="badge bg-secondary ms-2">{editLogs.length}</span>
                      )}
                    </label>
                    <div 
                      className="border rounded p-2" 
                      style={{ 
                        maxHeight: '150px', 
                        overflowY: 'auto',
                        backgroundColor: 'rgba(0,0,0,0.02)'
                      }}
                    >
                      {loadingLogs ? (
                        <div className="text-center text-muted py-2">
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Carregando histórico...
                        </div>
                      ) : editLogs.length === 0 ? (
                        <div className="text-center text-muted py-2 small">
                          Nenhuma alteração registrada
                        </div>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {editLogs.map((log, idx) => (
                            <div 
                              key={log.id || idx}
                              className="d-flex flex-column border-bottom pb-2"
                              style={{ fontSize: '0.85rem' }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">
                                  {new Date(log.timestamp).toLocaleDateString('pt-BR')} {new Date(log.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className={`badge ${log.change_amount >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                  {log.change_amount >= 0 ? '+' : ''}{formatNumber(log.change_amount)}
                                </span>
                              </div>
                              <div className="small">
                                <span className="text-muted">{formatNumber(log.old_value)}</span>
                                <i className="fas fa-arrow-right mx-2 text-muted" style={{ fontSize: '0.7rem' }}></i>
                                <span className="fw-medium">{formatNumber(log.new_value)}</span>
                              </div>
                              {log.notes && 
                               log.notes !== 'Atualização de progresso' && 
                               log.notes !== 'Progress update via form' && (
                                <div className="small text-muted fst-italic mt-1">
                                  "{log.notes}"
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-3">
                  <label htmlFor="editNotes" className="form-label">
                    <i className="fas fa-comment text-primary me-1"></i>
                    Nova Observação
                  </label>
                  <textarea 
                    className="form-control" 
                    id="editNotes"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows="2"
                    placeholder="Adicione observações sobre esta alteração (opcional)"
                    disabled={isSavingEdit}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer border-top pt-3">
                <button 
                  type="button" 
                  className="btn btn-light px-4" 
                  onClick={handleCloseEditModal}
                  disabled={isSavingEdit}
                >
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary px-4" 
                  onClick={handleSaveEdit}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Modal de Exclusão */}
      {deleteModalOpen && (
        <>
        <div className="modal-backdrop fade show" onClick={() => !isDeleting && setDeleteModalOpen(false)}></div>
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', color: '#fff', border: 'none' }}>
                <h5 className="modal-title">
                  <i className="fas fa-trash-alt me-2"></i>
                  Excluir Meta
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => !isDeleting && setDeleteModalOpen(false)}
                  disabled={isDeleting}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-warning mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Atenção:</strong> Esta ação não pode ser desfeita.
                </div>
                <p className="mb-4" style={{ color: '#1f2937' }}>
                  Como deseja excluir a meta <strong style={{ color: '#111827' }}>"{goal?.title}"</strong>?
                </p>
                
                {/* Opção 1: Apenas a meta atual */}
                <button
                  className="btn btn-outline-danger w-100 text-start p-3 mb-3"
                  style={{ borderRadius: '10px', border: '2px solid #fecaca' }}
                  onClick={() => confirmDelete(false)}
                  disabled={isDeleting}
                >
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="fas fa-file-alt" style={{ fontSize: '1.5rem', color: '#dc2626' }}></i>
                    </div>
                    <div>
                      <strong className="d-block" style={{ color: '#991b1b' }}>Excluir apenas esta meta</strong>
                      <small style={{ color: '#4b5563' }}>
                        Remove somente o período atual. As demais metas da família serão mantidas.
                      </small>
                    </div>
                  </div>
                </button>

                {/* Opção 2: Todas as metas relacionadas */}
                {goal?.parent_goal_id && (
                  <button
                    className="btn btn-outline-danger w-100 text-start p-3"
                    style={{ borderRadius: '10px', border: '2px solid #fecaca' }}
                    onClick={() => confirmDelete(true)}
                    disabled={isDeleting}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <i className="fas fa-layer-group" style={{ fontSize: '1.5rem', color: '#dc2626' }}></i>
                      </div>
                      <div>
                      <strong className="d-block" style={{ color: '#991b1b' }}>Excluir todas as metas relacionadas</strong>
                      <small style={{ color: '#4b5563' }}>
                        Remove esta meta e todas as metas com o mesmo objetivo (todos os períodos).
                      </small>
                      </div>
                    </div>
                  </button>
                )}
              </div>
              <div className="modal-footer" style={{ border: 'none', padding: '0.75rem 1.5rem 1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                {isDeleting && (
                  <span className="text-muted ms-2">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Excluindo...
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Modal de Conclusão (metas manuais) */}
      {concludeModalOpen && (
        <>
        <div className="modal-backdrop fade show" onClick={() => !isConcluding && setConcludeModalOpen(false)}></div>
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff', border: 'none' }}>
                <h5 className="modal-title">
                  <i className="fas fa-check-circle me-2"></i>
                  Concluir Meta
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => !isConcluding && setConcludeModalOpen(false)}
                  disabled={isConcluding}
                ></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-3">
                  Deseja concluir a meta <strong>"{goal?.title}"</strong>?
                </p>
                
                {goal?.start_date && (
                  <div className="alert alert-light mb-3 py-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-calendar-alt me-2 text-muted"></i>
                    Período: <strong>{formatDate(goal.start_date)}</strong> - <strong>{formatDate(goal.end_date)}</strong>
                  </div>
                )}

                {/* Opção para criar próxima meta */}
                <div 
                  className="form-check p-3 rounded mb-3"
                  style={{ 
                    background: concludeCreateNext ? '#f0fdf4' : '#f8f9fa',
                    border: concludeCreateNext ? '2px solid #16a34a' : '2px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setConcludeCreateNext(!concludeCreateNext)}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="concludeCreateNext"
                    checked={concludeCreateNext}
                    onChange={(e) => setConcludeCreateNext(e.target.checked)}
                    style={{ marginTop: '0.25rem' }}
                  />
                  <label className="form-check-label" htmlFor="concludeCreateNext" style={{ cursor: 'pointer' }}>
                    <strong>Criar nova meta para o próximo período</strong>
                    <br />
                    <small className="text-muted">
                      Uma nova meta será criada para o próximo {getPeriodLabel(goal?.period)?.toLowerCase() || 'período'} com os mesmos dados
                    </small>
                  </label>
                </div>

                {/* Campo de target value (visível quando criar próxima) */}
                {concludeCreateNext && (
                  <div className="mb-3" style={{ animation: 'fadeIn 0.3s ease' }}>
                    <label className="form-label fw-bold">
                      <i className="fas fa-bullseye me-1 text-primary"></i>
                      Valor alvo da nova meta
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      value={concludeTargetValue}
                      onChange={(e) => setConcludeTargetValue(e.target.value)}
                      placeholder="0,00"
                      style={{ fontSize: '1.1rem', fontWeight: '600' }}
                    />
                    <small className="text-muted">
                      Valor atual: {formatNumber(goal?.target_value)} {goal?.unit || ''}
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ border: 'none', padding: '0.75rem 1.5rem 1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setConcludeModalOpen(false)}
                  disabled={isConcluding}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={handleConcludeConfirm}
                  disabled={isConcluding}
                >
                  {isConcluding ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Concluindo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle me-2"></i>
                      {concludeCreateNext ? 'Concluir e Criar Nova' : 'Apenas Concluir'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

    </div>
  );
};

export default GoalDetail;
